import { collection, query, orderBy, limit, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

class IngredientSearchService {
  constructor() {
    this.ingredientsCache = [];
    this.lastCacheFetch = null;
    this.cacheValidityDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Search ingredients from Firestore database
   * @param {string} searchQuery - Search term (minimum 2 characters)
   * @returns {Promise<Array>} Array of matching ingredients
   */
  async searchIngredients(searchQuery) {
    if (searchQuery.length < 2) {
      console.warn('Query too short:', searchQuery);
      return [];
    }

    console.log('🔍 Searching ingredients for:', searchQuery);

    // Get ingredients from cache or fetch
    const ingredients = await this.getIngredientsFromCacheOrFetch();

    // Filter client-side by query
    const matches = ingredients.filter(ingredient => 
      this.ingredientMatches(ingredient, searchQuery)
    );

    console.log(`✅ Found ${matches.length} matching ingredients`);
    return matches;
  }

  /**
   * Get popular ingredients for suggestions
   * @param {number} limitCount - Number of ingredients to return
   * @returns {Promise<Array>} Array of popular ingredients
   */
  async getPopularIngredients(limitCount = 20) {
    console.log(`🌟 Fetching ${limitCount} popular ingredients`);
    
    const ingredients = await this.getIngredientsFromCacheOrFetch();
    const popular = ingredients.slice(0, limitCount);
    
    console.log(`✅ Loaded ${popular.length} popular ingredients`);
    return popular;
  }

  /**
   * Get allergen ingredients only
   * @returns {Promise<Array>} Array of allergen ingredients
   */
  async getAllergenIngredients() {
    console.log('⚠️ Fetching allergen ingredients');

    try {
      const q = query(
        collection(db, 'ingredients'),
        where('isAllergen', '==', true),
        orderBy('usageCount', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const allergens = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`✅ Loaded ${allergens.length} allergen ingredients`);
      return allergens;
    } catch (error) {
      console.error('Error fetching allergen ingredients:', error);
      return [];
    }
  }

  /**
   * Get ingredient by exact name
   * @param {string} name - Ingredient name
   * @returns {Promise<Object|null>} Ingredient document or null
   */
  async getIngredient(name) {
    const ingredientId = name.toLowerCase().replace(/\s+/g, '_');
    console.log('🔍 Fetching ingredient by ID:', ingredientId);

    try {
      const docRef = doc(db, 'ingredients', ingredientId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        console.log('✅ Found ingredient:', name);
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        console.log('❌ Ingredient not found:', name);
        return null;
      }
    } catch (error) {
      console.error('Error fetching ingredient:', error);
      return null;
    }
  }

  /**
   * Get ingredients from cache or fetch from Firestore
   * @private
   */
  async getIngredientsFromCacheOrFetch() {
    // Check if cache is valid
    if (
      this.lastCacheFetch &&
      Date.now() - this.lastCacheFetch < this.cacheValidityDuration &&
      this.ingredientsCache.length > 0
    ) {
      console.log(`📦 Using cached ingredients (${this.ingredientsCache.length} items)`);
      return this.ingredientsCache;
    }

    // Cache is stale or empty, fetch fresh data
    console.log('🌐 Fetching ingredients from Firestore');

    try {
      const q = query(
        collection(db, 'ingredients'),
        orderBy('usageCount', 'desc'),
        limit(100) // Get top 100 most common ingredients
      );

      const snapshot = await getDocs(q);
      const ingredients = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Update cache
      this.ingredientsCache = ingredients;
      this.lastCacheFetch = Date.now();

      console.log(`✅ Cached ${ingredients.length} ingredients`);
      return ingredients;
    } catch (error) {
      console.error('Error fetching ingredients:', error);
      return [];
    }
  }

  /**
   * Check if ingredient matches query
   * @private
   */
  ingredientMatches(ingredient, query) {
    const queryLower = query.toLowerCase();

    // Check main name
    if (ingredient.nameLowercase?.includes(queryLower)) {
      return true;
    }

    if (ingredient.name?.toLowerCase().includes(queryLower)) {
      return true;
    }

    // Check alternate names
    if (ingredient.alternateNames && Array.isArray(ingredient.alternateNames)) {
      return ingredient.alternateNames.some(alternate =>
        alternate.toLowerCase().includes(queryLower)
      );
    }

    return false;
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.ingredientsCache = [];
    this.lastCacheFetch = null;
    console.log('🗑️ Ingredient cache cleared');
  }

  /**
   * Refresh cache manually
   */
  async refreshCache() {
    this.clearCache();
    await this.getIngredientsFromCacheOrFetch();
  }

  /**
   * Get display text for ingredient
   */
  getDisplayText(ingredient) {
    const count = ingredient.usageCount > 0 ? ` (${ingredient.usageCount} products)` : '';
    return `${ingredient.name}${count}`;
  }
}

// Export singleton instance
export const ingredientSearchService = new IngredientSearchService();
